import React, { useState, useRef, useEffect } from 'react';
import { Trash2, Copy, RotateCcw, Code, Plus, ArrowRight, ArrowLeft, Play, Pause, SkipForward } from 'lucide-react';
import { count } from 'd3';
import { hex } from 'framer-motion';
import { useDarkMode } from 'context/DarkModeProvider';
import "../../assets/css/CreateYourOwn/queueBuilder.css"
import { useLanguage } from 'context/LanguageProvider';
import CodeHighlighter from 'components/CodeHighlighter';


const QueueBuilder = () => {
    const [queues, setQueues] = useState([
        { id: 1, name: 'Queue', elements: [], type: 'queue', color: '#667eea', maxSize: 10, front: 0, rear: 0 }
    ]);
    const [nextQueueId, setNextQueueId] = useState(2);
    const [selectedQueue, setSelectedQueue] = useState(1);
    const [newElement, setNewElement] = useState('');
    const [newPriority, setNewPriority] = useState(1);
    const [newQueueName, setNewQueueName] = useState('');
    const [newQueueType, setNewQueueType] = useState('queue');
    const [editingQueueId, setEditingQueueId] = useState(null);
    const [animatingOperation, setAnimatingOperation] = useState(null);
    const [codeLanguage, setCodeLanguage] = useState('javascript');
    const [showInfo, setShowInfo] = useState(false);
    const [maxSize, setMaxSize] = useState(10);
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedFromQueue, setDraggedFromQueue] = useState(false);
    const [dragOverZone, setDragOverZone] = useState(null);
    const containerRef = useRef(null);
    const [counter, setCounter] = useState(1);
    const [copied, setCopied] = useState(false);
    const { darkMode } = useDarkMode();
    const [editingElementId, setEditingElementId] = useState(null);
    const [editingElementValue, setEditingElementValue] = useState('');
    const [actionLog, setActionLog] = useState([]);
    const [peekingElement, setPeekingElement] = useState(null);

    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const colors = ['#667eea'];

    const handleAutoEnqueue = () => {
        const selectedType = queues.find(q => q.id === selectedQueue)?.type;
        if (!newElement.trim()) {
            // Για max/min priority βγάζουμε τυχαίο αριθμό ώστε το sorting να έχει νόημα
            const item = (selectedType === 'maxPriority' || selectedType === 'minPriority')
                ? String(Math.floor(Math.random() * 100) + 1)
                : `Item${counter}`;
            enqueue(item, newPriority);
            setCounter(counter + 1);
        } else {
            enqueue(newElement, newPriority);
        }
    };

    const saveElementEdit = (queueId, elementId) => {
        if (editingElementValue.trim()) {
            setQueues(prev => prev.map(queue =>
                queue.id === queueId
                    ? { ...queue, elements: queue.elements.map(el => el.id === elementId ? { ...el, value: editingElementValue.trim() } : el) }
                    : queue
            ));
        }
        setEditingElementId(null);
        setEditingElementValue('');
    };

    const animateOperation = (operation, queueId = null, result = null) => {
        setAnimatingOperation({ operation, queueId });
        setTimeout(() => setAnimatingOperation(null), 600);
    };

    const addQueue = (name = null, type = null) => {
        const newQueue = {
            id: nextQueueId,
            name: name || newQueueName || `${type === 'circular' ? 'Circular Queue' : type === 'maxPriority' ? 'Max Priority Queue' : type === 'minPriority' ? 'Min Priority Queue' : 'Queue'} ${nextQueueId}`,
            elements: [],
            type: type || newQueueType,
            color: colors[(nextQueueId - 1) % colors.length],
            maxSize: type === 'circular' ? maxSize : Infinity,
            front: 0,
            rear: 0
        };
        setQueues(prev => [...prev, newQueue]);
        setNextQueueId(prev => prev + 1);
        setSelectedQueue(newQueue.id);
        animateOperation('addQueue', newQueue.id);
        setNewQueueName('');
    };

    const renameQueue = (queueId, newName) => {
        if (newName.trim()) {
            setQueues(prev => prev.map(queue => queue.id === queueId ? { ...queue, name: newName.trim() } : queue));
        }
        setEditingQueueId(null);
    };

    const handleDragStart = (e, item, fromQueue = false) => {
        const itemWithQueue = fromQueue ? { ...item, queueId: selectedQueue } : item;
        setDraggedItem(itemWithQueue);
        setDraggedFromQueue(fromQueue);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', '');
        const dragImage = document.createElement('div');
        dragImage.innerHTML = item.value;
        dragImage.style.cssText = `position:absolute;top:-1000px;background:${item.color || colors[0]};padding:10px;border-radius:8px;border:2px solid #333;font-weight:bold;box-shadow:0 4px 8px rgba(0,0,0,0.2);`;
        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, 50, 25);
        setTimeout(() => document.body.removeChild(dragImage), 0);
    };

    const handleDragOver = (e, zone = null) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverZone(zone);
    };

    const handleDragLeave = () => setDragOverZone(null);

    const handleDrop = (e, dropZone, queueId = null) => {
        e.preventDefault();
        setDragOverZone(null);
        if (!draggedItem) return;
        if (dropZone === 'queue' && queueId) {
            if (!draggedFromQueue) {
                const targetQueue = queues.find(q => q.id === queueId);
                if (!targetQueue) return;
                const newItem = { id: Date.now(), value: draggedItem.value, priority: draggedItem.priority || 1 };
                if (targetQueue.type === 'circular') {
                    const nextRear = (targetQueue.rear + 1) % targetQueue.maxSize;
                    if (nextRear === targetQueue.front && targetQueue.elements.length > 0) { animateOperation('queueFull', queueId); return; }
                    setQueues(prev => prev.map(queue => queue.id === queueId ? { ...queue, elements: [...queue.elements, newItem], rear: nextRear } : queue));
                } else {
                    setQueues(prev => prev.map(queue => queue.id === queueId ? { ...queue, elements: sortElements([...queue.elements, newItem], queue.type) } : queue));
                }
                setActionLog(prev => [...prev, { type: 'enqueue', queueId, value: draggedItem.value, priority: draggedItem.priority || 1 }]);
                animateOperation('enqueue', queueId);
            }
        } else if (dropZone === 'garbage' && draggedFromQueue) {
            setQueues(prev => prev.map(queue => {
                if (queue.id === draggedItem.queueId) {
                    const updatedElements = queue.elements.filter(item => item.id !== draggedItem.id);
                    if (queue.type === 'circular') {
                        return { ...queue, elements: updatedElements, front: updatedElements.length === 0 ? 0 : queue.front, rear: updatedElements.length === 0 ? 0 : Math.max(0, queue.rear - 1) };
                    }
                    return { ...queue, elements: updatedElements };
                }
                return queue;
            }));
            setActionLog(prev => [...prev, { type: 'dequeue', queueId: draggedItem.queueId, value: draggedItem.value }]);
            animateOperation('dequeue', draggedItem.queueId);
        }
        setDraggedItem(null);
        setDraggedFromQueue(false);
    };

    const sortElements = (elements, type) => {
        if (type !== 'maxPriority' && type !== 'minPriority') return elements;

        return [...elements].sort((a, b) => {
            const aNum = parseFloat(a.value);
            const bNum = parseFloat(b.value);
            const bothNumeric = !isNaN(aNum) && !isNaN(bNum);

            if (bothNumeric) {
                return type === 'maxPriority' ? bNum - aNum : aNum - bNum;
            }
            // Mixed or string comparison — alphabetical
            const cmp = a.value.localeCompare(b.value, undefined, { sensitivity: 'base' });
            return type === 'maxPriority' ? -cmp : cmp;
        });
    };

    const enqueue = (element, priority = 1) => {
        if (!element.trim() || !selectedQueue) return;
        const targetQueue = queues.find(q => q.id === selectedQueue);
        if (!targetQueue) return;
        if (targetQueue.type === 'circular') {
            const nextRear = (targetQueue.rear + 1) % targetQueue.maxSize;
            if (nextRear === targetQueue.front && targetQueue.elements.length > 0) { animateOperation('queueFull', selectedQueue); return; }
            setQueues(prev => prev.map(queue => queue.id === selectedQueue ? { ...queue, elements: [...queue.elements, { value: element.trim(), priority: 1, id: Date.now() }], rear: nextRear } : queue));
        } else {
            const newEl = { value: element.trim(), priority, id: Date.now() };
            setQueues(prev => prev.map(queue => queue.id === selectedQueue
                ? { ...queue, elements: sortElements([...queue.elements, newEl], queue.type) }
                : queue
            ));
        }
        setActionLog(prev => [...prev, { type: 'enqueue', queueId: selectedQueue, value: element.trim(), priority }]);
        animateOperation('enqueue', selectedQueue);
        setNewElement('');
        setNewPriority(1);
    };

    const dequeue = (queueId) => {
        const targetQueue = queues.find(q => q.id === queueId);
        if (!targetQueue || targetQueue.elements.length === 0) { animateOperation('queueEmpty', queueId); return; }
        let dequeuedElement;
        if (targetQueue.type === 'circular') {
            dequeuedElement = targetQueue.elements[0];
            setQueues(prev => prev.map(queue => queue.id === queueId ? { ...queue, elements: queue.elements.slice(1), front: queue.elements.length > 1 ? (queue.front + 1) % queue.maxSize : 0 } : queue));
        } else {
            dequeuedElement = targetQueue.elements[0];
            setQueues(prev => prev.map(queue => queue.id === queueId ? { ...queue, elements: queue.elements.slice(1) } : queue));
        }
        setActionLog(prev => [...prev, { type: 'dequeue', queueId, value: dequeuedElement.value }]);
    };

    const peek = (queueId) => {
        const targetQueue = queues.find(q => q.id === queueId);
        if (!targetQueue || targetQueue.elements.length === 0) { animateOperation('queueEmpty', queueId); return; }
        const frontElement = targetQueue.elements[0];
        setAnimatingOperation({ operation: 'peek', queueId });
        setTimeout(() => setAnimatingOperation(null), 600);
        setPeekingElement(frontElement.id);
        setTimeout(() => setPeekingElement(null), 1000);
        setActionLog(prev => [...prev, { type: 'peek', queueId, value: frontElement.value }]);
    };

    const clearQueue = (queueId) => {
        setQueues(prev => prev.map(queue => queue.id === queueId ? { ...queue, elements: [], front: 0, rear: 0 } : queue));
        setActionLog(prev => [...prev, { type: 'clear', queueId }]);
        animateOperation('clear', queueId);
        setCounter(0);
    };

    const deleteQueue = (queueId) => {
        if (queues.length <= 1) return;
        setQueues(prev => prev.filter(q => q.id !== queueId));
        if (selectedQueue === queueId) setSelectedQueue(queues.find(q => q.id !== queueId)?.id || queues[0].id);
        animateOperation('deleteQueue', queueId);
    };

    const generateCode = () => {
        const getVarName = (queue, lang) => lang === 'python' ? queue.name.replace(/\s+/g, '_').toLowerCase() : queue.name.replace(/\s+/g, '').toLowerCase();
        const hasPriority = (q) => q.type === 'maxPriority' || q.type === 'minPriority';
        const renderActions = (queue, lang) => {
            const varName = getVarName(queue, lang);
            return actionLog.filter(a => a.queueId === queue.id).map(action => {
                if (action.type === 'enqueue') {
                    if (lang === 'javascript') return hasPriority(queue) ? `${varName}.enqueue("${action.value}", ${action.priority});` : `${varName}.enqueue("${action.value}");`;
                    if (lang === 'python')     return hasPriority(queue) ? `${varName}.enqueue("${action.value}", ${action.priority})` : `${varName}.enqueue("${action.value}")`;
                    if (lang === 'java')       return hasPriority(queue) ? `        ${varName}.enqueue("${action.value}", ${action.priority});` : `        ${varName}.enqueue("${action.value}");`;
                    if (lang === 'cpp')        return hasPriority(queue) ? `    ${varName}.enqueue("${action.value}", ${action.priority});` : `    ${varName}.enqueue("${action.value}");`;
                }
                if (action.type === 'dequeue') {
                    if (lang === 'javascript') return `${varName}.dequeue(); // removed "${action.value}"`;
                    if (lang === 'python')     return `${varName}.dequeue()  # removed "${action.value}"`;
                    if (lang === 'java')       return `        ${varName}.dequeue(); // removed "${action.value}"`;
                    if (lang === 'cpp')        return `    ${varName}.dequeue(); // removed "${action.value}"`;
                }
                if (action.type === 'peek') {
                    if (lang === 'javascript') return `${varName}.front(); // peeked "${action.value}"`;
                    if (lang === 'python')     return `${varName}.front()  # peeked "${action.value}"`;
                    if (lang === 'java')       return `        ${varName}.front(); // peeked "${action.value}"`;
                    if (lang === 'cpp')        return `    ${varName}.front(); // peeked "${action.value}"`;
                }
                if (action.type === 'clear') {
                    if (lang === 'javascript') return `// Queue was cleared at this point`;
                    if (lang === 'python')     return `# Queue was cleared at this point`;
                    if (lang === 'java')       return `        // Queue was cleared at this point`;
                    if (lang === 'cpp')        return `    // Queue was cleared at this point`;
                }
                return '';
            }).join('\n');
        };

        const getInitLine = (queue, lang, varName) => {
            if (queue.type === 'maxPriority') {
                if (lang === 'javascript') return `const ${varName} = new MaxPriorityQueue();`;
                if (lang === 'python')     return `${varName} = MaxPriorityQueue()`;
                if (lang === 'java')       return `        MaxPriorityQueue ${varName} = new MaxPriorityQueue();`;
                if (lang === 'cpp')        return `    MaxPriorityQueue ${varName};`;
            }
            if (queue.type === 'minPriority') {
                if (lang === 'javascript') return `const ${varName} = new MinPriorityQueue();`;
                if (lang === 'python')     return `${varName} = MinPriorityQueue()`;
                if (lang === 'java')       return `        MinPriorityQueue ${varName} = new MinPriorityQueue();`;
                if (lang === 'cpp')        return `    MinPriorityQueue ${varName};`;
            }
            if (queue.type === 'circular') {
                const sz = queue.maxSize === Infinity ? 10 : queue.maxSize;
                if (lang === 'javascript') return `const ${varName} = new CircularQueue(${sz});`;
                if (lang === 'python')     return `${varName} = CircularQueue(${sz})`;
                if (lang === 'java')       return `        CircularQueue ${varName} = new CircularQueue(${sz});`;
                if (lang === 'cpp')        return `    CircularQueue ${varName}(${sz});`;
            }
            // plain queue
            if (lang === 'javascript') return `const ${varName} = new Queue();`;
            if (lang === 'python')     return `${varName} = Queue()`;
            if (lang === 'java')       return `        Queue ${varName} = new Queue();`;
            if (lang === 'cpp')        return `    Queue ${varName};`;
        };

        const codes = {
            javascript: `// Queue Implementations in JavaScript

class Queue {
    constructor() { this.items = []; }
    enqueue(element) { this.items.push(element); }
    dequeue() { if (this.isEmpty()) return null; return this.items.shift(); }
    front() { if (this.isEmpty()) return null; return this.items[0]; }
    isEmpty() { return this.items.length === 0; }
    size() { return this.items.length; }
}

class MaxPriorityQueue {
    constructor() { this.items = []; }
    enqueue(element, priority) { this.items.push({ element, priority }); this.items.sort((a, b) => b.priority - a.priority); }
    dequeue() { if (this.isEmpty()) return null; return this.items.shift().element; }
    front() { if (this.isEmpty()) return null; return this.items[0].element; }
    isEmpty() { return this.items.length === 0; }
    size() { return this.items.length; }
}

class MinPriorityQueue {
    constructor() { this.items = []; }
    enqueue(element, priority) { this.items.push({ element, priority }); this.items.sort((a, b) => a.priority - b.priority); }
    dequeue() { if (this.isEmpty()) return null; return this.items.shift().element; }
    front() { if (this.isEmpty()) return null; return this.items[0].element; }
    isEmpty() { return this.items.length === 0; }
    size() { return this.items.length; }
}

class CircularQueue {
    constructor(maxSize) { this.maxSize = maxSize; this.items = new Array(maxSize); this.front = 0; this.rear = 0; this.count = 0; }
    enqueue(element) { if (this.isFull()) return false; this.items[this.rear] = element; this.rear = (this.rear + 1) % this.maxSize; this.count++; return true; }
    dequeue() { if (this.isEmpty()) return null; const e = this.items[this.front]; this.items[this.front] = undefined; this.front = (this.front + 1) % this.maxSize; this.count--; return e; }
    peek() { if (this.isEmpty()) return null; return this.items[this.front]; }
    isEmpty() { return this.count === 0; }
    isFull() { return this.count === this.maxSize; }
    size() { return this.count; }
}

// Example usage:
${queues.map(q => { const v = getVarName(q, 'javascript'); return `${getInitLine(q, 'javascript', v)}\n${renderActions(q, 'javascript')}`; }).join('\n\n')}`,

            python: `# Queue Implementations in Python
import heapq
from collections import deque

class Queue:
    def __init__(self): self.items = deque()
    def enqueue(self, element): self.items.append(element)
    def dequeue(self): return self.items.popleft() if not self.is_empty() else None
    def front(self): return self.items[0] if not self.is_empty() else None
    def is_empty(self): return len(self.items) == 0
    def size(self): return len(self.items)

class MaxPriorityQueue:
    def __init__(self): self.items = []; self.counter = 0
    def enqueue(self, element, priority):
        heapq.heappush(self.items, (-priority, self.counter, element)); self.counter += 1
    def dequeue(self): return heapq.heappop(self.items)[2] if not self.is_empty() else None
    def front(self): return self.items[0][2] if not self.is_empty() else None
    def is_empty(self): return len(self.items) == 0
    def size(self): return len(self.items)

class MinPriorityQueue:
    def __init__(self): self.items = []; self.counter = 0
    def enqueue(self, element, priority):
        heapq.heappush(self.items, (priority, self.counter, element)); self.counter += 1
    def dequeue(self): return heapq.heappop(self.items)[2] if not self.is_empty() else None
    def front(self): return self.items[0][2] if not self.is_empty() else None
    def is_empty(self): return len(self.items) == 0
    def size(self): return len(self.items)

class CircularQueue:
    def __init__(self, max_size):
        self.max_size = max_size; self.items = [None]*max_size; self.front = 0; self.rear = 0; self.count = 0
    def enqueue(self, element):
        if self.is_full(): return False
        self.items[self.rear] = element; self.rear = (self.rear+1)%self.max_size; self.count += 1; return True
    def dequeue(self):
        if self.is_empty(): return None
        e = self.items[self.front]; self.items[self.front] = None; self.front = (self.front+1)%self.max_size; self.count -= 1; return e
    def peek(self): return self.items[self.front] if not self.is_empty() else None
    def is_empty(self): return self.count == 0
    def is_full(self): return self.count == self.max_size
    def size(self): return self.count

# Example usage:
${queues.map(q => { const v = getVarName(q, 'python'); return `${getInitLine(q, 'python', v)}\n${renderActions(q, 'python')}`; }).join('\n\n')}`,

            java: `// Queue Implementations in Java
import java.util.ArrayList;
import java.util.LinkedList;

public class Main {
    static class Queue {
        LinkedList<String> items = new LinkedList<>();
        void enqueue(String e) { items.addLast(e); }
        String dequeue() { return isEmpty() ? null : items.removeFirst(); }
        String front() { return isEmpty() ? null : items.getFirst(); }
        boolean isEmpty() { return items.isEmpty(); }
        int size() { return items.size(); }
    }
    static class MaxPriorityQueue {
        static class El { String v; int p; El(String v,int p){this.v=v;this.p=p;} }
        ArrayList<El> items = new ArrayList<>();
        void enqueue(String v, int p) { items.add(new El(v,p)); items.sort((a,b)->b.p-a.p); }
        String dequeue() { return isEmpty()?null:items.remove(0).v; }
        String front() { return isEmpty()?null:items.get(0).v; }
        boolean isEmpty() { return items.isEmpty(); }
        int size() { return items.size(); }
    }
    static class MinPriorityQueue {
        static class El { String v; int p; El(String v,int p){this.v=v;this.p=p;} }
        ArrayList<El> items = new ArrayList<>();
        void enqueue(String v, int p) { items.add(new El(v,p)); items.sort((a,b)->a.p-b.p); }
        String dequeue() { return isEmpty()?null:items.remove(0).v; }
        String front() { return isEmpty()?null:items.get(0).v; }
        boolean isEmpty() { return items.isEmpty(); }
        int size() { return items.size(); }
    }
    static class CircularQueue {
        String[] items; int front,rear,count,maxSize;
        CircularQueue(int sz){maxSize=sz;items=new String[sz];}
        boolean enqueue(String e){if(isFull())return false;items[rear]=e;rear=(rear+1)%maxSize;count++;return true;}
        String dequeue(){if(isEmpty())return null;String e=items[front];items[front]=null;front=(front+1)%maxSize;count--;return e;}
        String peek(){return isEmpty()?null:items[front];}
        boolean isEmpty(){return count==0;} boolean isFull(){return count==maxSize;} int size(){return count;}
    }
    public static void main(String[] args) {
${queues.map(q => { const v = getVarName(q, 'java'); return `${getInitLine(q, 'java', v)}\n${renderActions(q, 'java')}`; }).join('\n\n')}
    }
}`,

            cpp: `// Queue Implementations in C++
#include <iostream>
#include <queue>
#include <vector>
#include <algorithm>
#include <string>
using namespace std;

struct Queue {
    queue<string> items;
    void enqueue(const string& e) { items.push(e); }
    string dequeue() { if(isEmpty()) return ""; string e=items.front(); items.pop(); return e; }
    string front() const { return isEmpty()?"":items.front(); }
    bool isEmpty() const { return items.empty(); }
    int size() const { return (int)items.size(); }
};
struct PQEl { string v; int p; };
struct MaxPriorityQueue {
    vector<PQEl> items;
    void enqueue(const string& v, int p) { items.push_back({v,p}); sort(items.begin(),items.end(),[](const PQEl&a,const PQEl&b){return b.p<a.p;}); }
    string dequeue() { if(isEmpty()) return ""; string v=items.front().v; items.erase(items.begin()); return v; }
    string front() const { return isEmpty()?"":items.front().v; }
    bool isEmpty() const { return items.empty(); }
    int size() const { return (int)items.size(); }
};
struct MinPriorityQueue {
    vector<PQEl> items;
    void enqueue(const string& v, int p) { items.push_back({v,p}); sort(items.begin(),items.end(),[](const PQEl&a,const PQEl&b){return a.p<b.p;}); }
    string dequeue() { if(isEmpty()) return ""; string v=items.front().v; items.erase(items.begin()); return v; }
    string front() const { return isEmpty()?"":items.front().v; }
    bool isEmpty() const { return items.empty(); }
    int size() const { return (int)items.size(); }
};
struct CircularQueue {
    vector<string> items; int frontIdx,rear,count,maxSize;
    CircularQueue(int sz):maxSize(sz),frontIdx(0),rear(0),count(0){items.resize(sz);}
    bool enqueue(const string& e){if(isFull())return false;items[rear]=e;rear=(rear+1)%maxSize;count++;return true;}
    string dequeue(){if(isEmpty())return "";string v=items[frontIdx];items[frontIdx]="";frontIdx=(frontIdx+1)%maxSize;count--;return v;}
    string peek()const{return isEmpty()?"":items[frontIdx];}
    bool isEmpty()const{return count==0;} bool isFull()const{return count==maxSize;} int size()const{return count;}
};
int main() {
${queues.map(q => { const v = getVarName(q, 'cpp'); return `${getInitLine(q, 'cpp', v)}\n${renderActions(q, 'cpp')}`; }).join('\n\n')}
    return 0;
}`
        };
        return codes[codeLanguage];
    };

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.ctrlKey || e.metaKey) {
                switch (e.key) {
                    case 'Enter': e.preventDefault(); if (newElement.trim()) enqueue(newElement, newPriority); break;
                    case 'n':    e.preventDefault(); addQueue(); break;
                }
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [newElement, newPriority, queues]);

    const containerStyle = {
        display: 'flex', gap: '30px', padding: '30px', flexWrap: 'wrap',
        background: darkMode ? '#0f172a' : 'var(--lightModeBack)', minHeight: '100vh',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    };
    const visualizationContainerStyle = {
        flex: 1, background: darkMode ? '#1E293B' : 'var(--customBuildBack)',
        backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '30px',
        boxShadow: '0 25px 50px rgba(0,0,0,0.15)', width: '100%', minWidth: 0, height: "120%",
        border: '1px solid rgba(255,255,255,0.2)',
        borderColor: darkMode ? 'rgba(122,142,255,0.3)' : 'rgba(0,0,0,0.2)',
    };
    const titleStyle = {
        fontSize: '2.5rem', fontWeight: '800', background: "rgb(122,142,255)",
        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        textAlign: 'center', marginBottom: '30px', letterSpacing: '-0.02em'
    };
    const buttonStyle = {
        padding: '16px 24px', borderRadius: '16px', border: 'none', fontSize: '1rem',
        fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease',
        fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    };
    const inputStyle = {
        padding: '16px 20px', border: '2px solid #e5e7eb', borderRadius: '16px',
        fontSize: '1rem', background: 'white', transition: 'all 0.2s ease', fontFamily: 'inherit'
    };

    // ── Circular ring visualization ─────────────────────────────────────────
    const renderCircularVisualization = (queue) => {
        const svgSize = 340;
        const cx = svgSize / 2;
        const cy = svgSize / 2;
        const trackR = 120;
        const nodeR  = 28;
        const slots  = queue.maxSize;

        if (queue.elements.length === 0) {
            return (
                <div style={{
                    width: '100%', textAlign: 'center', color: '#9ca3af',
                    fontSize: '1.1rem', fontWeight: '500',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{ fontSize: '3rem', marginBottom: '10px' }}>📦</div>
                    <div>{selLanguage ? "Άδεια ουρά" : "Empty Queue"}</div>
                    <div style={{ fontSize: '0.9rem', marginTop: '8px', opacity: 0.7 }}>
                        {selLanguage ? "Χρησιμοποίησε τις λειτουργίες" : "Use controls to add elements"}
                    </div>
                </div>
            );
        }

        const slotData = Array.from({ length: slots }, (_, i) => ({
            index: i,
            element: queue.elements[i] || null,
            isFront: i === 0 && queue.elements.length > 0,
            isRear:  i === queue.elements.length - 1 && queue.elements.length > 0,
        }));

        return (
            <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
                <defs>
                    {/* Gradient για peek state */}
                    <linearGradient id={`peekGrad-${queue.id}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={queue.color} />
                        <stop offset="100%" stopColor="#ffffff" />
                    </linearGradient>
                </defs>

                {/* Dashed track ring */}
                <circle cx={cx} cy={cy} r={trackR} fill="none"
                    stroke="rgba(102,126,234,0.2)" strokeWidth="2" strokeDasharray="6 4" />

                {/* Nodes */}
                {slotData.map((slot, i) => {
                    const angle  = (2 * Math.PI * i / slots) - Math.PI / 2;
                    const x      = cx + trackR * Math.cos(angle);
                    const y      = cy + trackR * Math.sin(angle);
                    const filled = !!slot.element;
                    const isPeek = filled && peekingElement === slot.element?.id;

                    const fillColor = isPeek ? `linear-gradient(135deg, ${queue.color}, #ffffff)`
                        : slot.isFront ? '#6260df96'
                        : filled ? queue.color
                        : 'rgba(209,213,219,0.25)';

                    const strokeColor = slot.isFront ? 'rgba(255,255,255,0.06)'
                        : slot.isRear ? '#f59e0b'
                        : filled ? `${queue.color}cc`
                        : 'rgba(209,213,219,0.5)';

                    const strokeWidth = isPeek ? 3
                        : slot.isFront ? 3
                        : slot.isRear ? 2.5
                        : 1.5;

                    const nodeFilter = isPeek
                        ? '0 8px 25px rgba(102,126,234,0.4)'
                        : slot.isFront
                            ? '0 8px 25px rgba(0,0,0,0.15)'
                            : '0 4px 12px rgba(0,0,0,0.15)';

                    // Label position (outside the ring)
                    const labelX = cx + (trackR + nodeR + 14) * Math.cos(angle);
                    const labelY = cy + (trackR + nodeR + 14) * Math.sin(angle);

                    return (
                        <g key={i}
                            style={{ cursor: filled ? 'grab' : 'default' }}
                            onDoubleClick={() => {
                                if (filled) { setEditingElementId(slot.element.id); setEditingElementValue(slot.element.value); }
                            }}
                        >
                            {/* Node — rounded rect */}
                            <rect
                                x={x - nodeR} y={y - nodeR}
                                width={nodeR * 2} height={nodeR * 2}
                                rx="8" ry="8"
                                fill={isPeek ? `url(#peekGrad-${queue.id})` : fillColor}
                                stroke={isPeek ? 'rgba(255,255,255,0.6)' : strokeColor}
                                strokeWidth={strokeWidth}
                                style={{
                                    transition: 'all 0.3s ease',
                                    filter: isPeek ? `drop-shadow(0 0 8px ${queue.color})` : 'none',
                                }}
                            />

                            {/* Slot index */}
                            <text x={labelX} y={labelY}
                                textAnchor="middle" dominantBaseline="central"
                                fontSize="10" fill="rgba(100,116,139,0.7)" fontWeight="600">
                                {i}
                            </text>

                            {/* Value or empty marker */}
                            {filled ? (
                                editingElementId === slot.element.id ? (
                                    <foreignObject x={x - 26} y={y - 12} width="52" height="24">
                                        <input
                                            autoFocus
                                            value={editingElementValue}
                                            onChange={(e) => setEditingElementValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveElementEdit(queue.id, slot.element.id);
                                                if (e.key === 'Escape') { setEditingElementId(null); setEditingElementValue(''); }
                                            }}
                                            onBlur={() => saveElementEdit(queue.id, slot.element.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                width: '100%', height: '100%', background: 'transparent',
                                                border: 'none', color: 'white', fontWeight: '700',
                                                fontSize: '11px', textAlign: 'center', outline: 'none', fontFamily: 'inherit',
                                            }}
                                        />
                                    </foreignObject>
                                ) : (
                                    <text x={x} y={y} textAnchor="middle" dominantBaseline="central"
                                        fontSize="11" fontWeight="700" fill="white"
                                        style={{ pointerEvents: 'none', userSelect: 'none' }}>
                                        {slot.element.value.length > 5 ? slot.element.value.slice(0, 4) + '…' : slot.element.value}
                                    </text>
                                )
                            ) : (
                                <text x={x} y={y} textAnchor="middle" dominantBaseline="central"
                                    fontSize="12" fill="rgba(156,163,175,0.5)"
                                    style={{ pointerEvents: 'none' }}>∅</text>
                            )}

                            {/* FRONT / REAR badge next to node */}
                            {slot.isFront && (
                                <text x={x} y={y - nodeR - 7}
                                    textAnchor="middle" dominantBaseline="central"
                                    fontSize="9" fontWeight="700" fill="#10b981"
                                    style={{ pointerEvents: 'none' }}>
                                    {selLanguage ? 'ΑΡΧΗ' : 'FRONT'}
                                </text>
                            )}
                            {slot.isRear && !slot.isFront && (
                                <text x={x} y={y + nodeR + 10}
                                    textAnchor="middle" dominantBaseline="central"
                                    fontSize="9" fontWeight="700" fill="#f59e0b"
                                    style={{ pointerEvents: 'none' }}>
                                    {selLanguage ? 'ΤΕΛΟΣ' : 'REAR'}
                                </text>
                            )}
                        </g>
                    );
                })}

                {/* Center info — count only */}
                <text x={cx} y={cy} textAnchor="middle" fontSize="12"
                    fill="rgba(100,116,139,0.7)">{queue.elements.length}/{queue.maxSize}</text>
            </svg>
        );
    };

    return (
        <div className="queue-builder-container" style={containerStyle}>
            <div className="visualizationContainerStyle" style={visualizationContainerStyle}>
                <h1 style={titleStyle}>{selLanguage ? "Δημιουργία ουράς" : "Queue builder"}</h1>

                {/* Controls */}
                <div className="qb-controls" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '15px', marginBottom: '20px' }}>
                    <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: '600', color: darkMode ? 'white' : 'black', marginBottom: '8px', letterSpacing: '0.025em', display: 'block' }}>
                            {selLanguage ? "Τύπος ουράς" : "Queue Type"}
                        </label>
                        <select className='typeofqueue'
                            value={queues.find(q => q.id === selectedQueue)?.type || newQueueType}
                            onChange={(e) => {
                                const newType = e.target.value;
                                setNewQueueType(newType);
                                setQueues(prev => prev.map(queue =>
                                    queue.id === selectedQueue
                                        ? { ...queue, type: newType, elements: [], front: 0, rear: 0, maxSize: newType === 'circular' ? maxSize : Infinity }
                                        : queue
                                ));
                            }}
                            style={{ ...inputStyle, width: '100%', cursor: 'pointer' }}>
                            <option value="queue">Queue</option>
                            <option value="maxPriority">Max Priority Queue</option>
                            <option value="minPriority">Min Priority Queue</option>
                            <option value="circular">Circular Queue</option>
                        </select>
                    </div>
                    {newQueueType === 'circular' && (
                        <div>
                            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'white', marginBottom: '8px', letterSpacing: '0.025em', display: 'block' }}>Max Size</label>
                            <input type="number" value={maxSize}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    setMaxSize(raw === '' ? '' : parseInt(raw) || '');
                                }}
                                onBlur={(e) => {
                                    const val = Math.max(1, parseInt(e.target.value) || 1);
                                    setMaxSize(val);
                                    setQueues(prev => prev.map(queue =>
                                        queue.id === selectedQueue && queue.type === 'circular'
                                            ? { ...queue, maxSize: val, elements: [], front: 0, rear: 0 }
                                            : queue
                                    ));
                                }}
                                min="1" max="20" style={{ ...inputStyle, width: '100%' }} />
                        </div>
                    )}
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button className='commoninfoButton'
                            style={{ ...buttonStyle, background: darkMode ? "#1e293b" : "var(--customBuildButton)", fontFamily: "Poppins, sans-serif", color: "#7a8effff", border: "2px solid #7a8effff", width: '56px', height: '56px', marginTop: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)}>💡</button>
                        {showInfo && (
                            <div style={{ position: 'absolute', top: '100%', left: '8%', transform: 'translateX(-50%)', marginTop: '10px', padding: '20px', background: 'linear-gradient(135deg, rgba(30,41,59,0.98), rgba(51,65,85,0.98))', backdropFilter: 'blur(20px)', borderRadius: '16px', border: '1px solid rgba(122,142,255,0.3)', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', zIndex: 1000, minWidth: '320px' }}>
                                <div style={{ position: 'absolute', top: '-8px', left: '40%', transform: 'translateX(-30%)', width: '0', height: '0', borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid rgba(30,41,59,0.98)' }} />
                                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#ffffff', marginBottom: '15px', textAlign: 'center' }}>{selLanguage ? "Οδηγίες" : "Instructions"}</h3>
                                <div style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: '1.6' }}>
                                    <div><strong>Ctrl+Enter:</strong>{selLanguage ? " Προσθήκη στοιχείου" : " Add element"}</div>
                                    <div><strong>Drag & Drop:</strong> {selLanguage ? "Προσθήκη στην ουρά" : " Add to queue"}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Element Input */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px', marginBottom: '20px' }}>
                    <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: '600', color: darkMode ? 'white' : 'black', marginBottom: '8px', letterSpacing: '0.025em', display: 'block' }}>
                            {selLanguage ? "Προσθήκη στοιχείου" : "Create New Element"}
                        </label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input type="text" value={newElement} onChange={(e) => setNewElement(e.target.value)}
                                onKeyPress={(e) => { if (e.key === 'Enter') handleAutoEnqueue(); }}
                                placeholder="Enter value..."
                                style={{ ...inputStyle, width: '74.5%' }}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                className="custom-input" />
                        </div>
                        <style>{`.custom-input::placeholder { color: #363a42ff; opacity: 1; }`}</style>
                    </div>
                </div>

                {/* Operation Buttons */}
                <div className="qb-op-buttons" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '20px' }}>
                    {[
                        { label: selLanguage ? "Προσθήκη" : "Enqueue", onClick: handleAutoEnqueue, disabled: false },
                        { label: selLanguage ? "Αφαίρεση" : "Dequeue", onClick: () => selectedQueue && dequeue(selectedQueue), disabled: !selectedQueue },
                        { label: "Peek",                                onClick: () => selectedQueue && peek(selectedQueue),    disabled: !selectedQueue },
                        { label: selLanguage ? "Καθαρισμός" : "Reset", onClick: () => selectedQueue && clearQueue(selectedQueue), disabled: !selectedQueue },
                    ].map(({ label, onClick, disabled }) => (
                        <button key={label} onClick={onClick} disabled={disabled} className='commonCreateButton'
                            style={{ ...buttonStyle, background: darkMode ? "#1e293b" : "var(--customBuildButton)", fontFamily: "Poppins, sans-serif", color: "#7a8effff", border: "2px solid #7a8effff", opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                            onMouseOver={(e) => !disabled && (e.currentTarget.style.transform = 'translateY(-2px)')}
                            onMouseOut={(e) => (e.currentTarget.style.transform = 'translateY(0)')}>
                            {label}
                        </button>
                    ))}
                </div>

                {/* ── Queue Visualization ── */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2%', marginTop: '5%', width: '100%' }}>
                    {queues.map((queue) => {
                        const isCircular = queue.type === 'circular';
                        return (
                            <div
                                key={queue.id}
                                onClick={() => setSelectedQueue(queue.id)}
                                onDragOver={(e) => handleDragOver(e, 'queue')}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, 'queue', queue.id)}
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    maxWidth: '800px',
                                    minHeight: isCircular ? '380px' : '320px',
                                    height: isCircular ? 'auto' : '320px',
                                    background: "linear-gradient(135deg, rgb(245,247,250) 0%, rgb(195,207,226) 100%)",
                                    borderRadius: '20px',
                                    padding: '20px',
                                    border: selectedQueue === queue.id
                                        ? darkMode ? `2px solid ${queue.color}` : `1px solid #201f1fff`
                                        : dragOverZone === 'queue' ? '2px dashed #4facfe' : '2px solid #e5e7eb',
                                    boxShadow: selectedQueue === queue.id ? `0 10px 25px ${queue.color}40` : 'inset 0 2px 8px rgba(0,0,0,0.06)',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    transform: animatingOperation?.queueId === queue.id ? 'scale(1.02)' : 'scale(1)',
                                    marginBottom: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {isCircular ? (
                                    renderCircularVisualization(queue)
                                ) : (
                                    <>
                                        {/* Queue Base bar */}
                                        <div style={{ position: 'absolute', bottom: '15px', left: '15px', right: '15px', height: '8px', background: "linear-gradient(135deg, #cbd5e1, #94a3b8)", borderRadius: '4px', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)' }} />

                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', width: '100%', gap: '1%', paddingBottom: '20px', paddingTop: '20px', overflowX: 'auto' }}>
                                            {queue.elements.length === 0 ? (
                                                <div style={{ width: '100%', textAlign: 'center', color: '#9ca3af', fontSize: '1.1rem', fontWeight: '500', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
                                                    <div style={{ fontSize: '3rem', marginBottom: '10px' }}>📦</div>
                                                    <div>{selLanguage ? "Άδεια ουρά" : "Empty Queue"}</div>
                                                    <div style={{ fontSize: '0.9rem', marginTop: '8px', opacity: 0.7 }}>{selLanguage ? "Σύρε εδώ τα στοιχεία ή χρησιμοποίησε τις λειτουργίες" : "Drag items here or use controls"}</div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div style={{ background: 'rgba(16,185,129,0.9)', color: 'white', padding: '4px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '600', writingMode: 'vertical-rl', textOrientation: 'mixed', minHeight: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{selLanguage ? "Αρχή" : "FRONT"}</div>
                                                    {queue.elements.map((element, index) => (
                                                        <div key={element.id}
                                                            draggable={editingElementId !== element.id}
                                                            onDragStart={(e) => editingElementId !== element.id && handleDragStart(e, element, true)}
                                                            onDoubleClick={() => { setEditingElementId(element.id); setEditingElementValue(element.value); }}
                                                            style={{ padding: '16px 20px', background: peekingElement === element.id ? `linear-gradient(135deg, ${queue.color}, #ffffff)` : index === 0 ? `#6260df96` : `linear-gradient(135deg, ${queue.color}, ${queue.color}dd)`, transform: peekingElement === element.id ? 'scale(1.02) translateY(-3px)' : 'scale(1)', boxShadow: peekingElement === element.id ? '0 8px 25px rgba(102,126,234,0.4)' : index === 0 ? '0 8px 25px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '12px', border: peekingElement === element.id ? '3px solid rgba(255,255,255,0.6)' : index === 0 ? '3px solid rgba(255,255,255,0.06)' : '2px solid rgba(255,255,255,0.3)', cursor: editingElementId === element.id ? 'text' : 'grab', textAlign: 'center', fontWeight: '600', fontSize: '1rem', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)', position: 'relative', minWidth: '80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                                            {editingElementId === element.id ? (
                                                                <input autoFocus value={editingElementValue} onChange={(e) => setEditingElementValue(e.target.value)}
                                                                    onKeyDown={(e) => { if (e.key === 'Enter') saveElementEdit(queue.id, element.id); if (e.key === 'Escape') { setEditingElementId(null); setEditingElementValue(''); } }}
                                                                    onBlur={() => saveElementEdit(queue.id, element.id)}
                                                                    style={{ background: 'transparent', border: 'none', borderRadius: '0', color: 'white', fontWeight: '600', fontSize: '1rem', textAlign: 'center', width: '80px', padding: '2px 4px', outline: 'none', fontFamily: 'inherit', caretColor: 'white' }}
                                                                    onClick={(e) => e.stopPropagation()} />
                                                            ) : (
                                                                <>
                                                                    <div>{element.value}</div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                    <div style={{ background: 'rgba(245,158,11,0.9)', color: 'white', padding: '4px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '600', writingMode: 'vertical-rl', textOrientation: 'mixed', minHeight: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{selLanguage ? "Τέλος" : "REAR"}</div>
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Operation Indicator */}
                                {animatingOperation?.queueId === queue.id && (
                                    <div style={{ position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)', padding: '8px 16px', background: 'rgba(102,126,234,0.9)', backdropFilter: 'blur(10px)', borderRadius: '20px', color: 'white', fontWeight: '600', fontSize: '0.9rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.2)', zIndex: 20, whiteSpace: 'nowrap' }}>
                                        {animatingOperation.operation === 'addQueue'    && 'ADDING QUEUE'}
                                        {animatingOperation.operation === 'enqueue'     && 'ENQUEUING'}
                                        {animatingOperation.operation === 'dequeue'     && 'DEQUEUING'}
                                        {animatingOperation.operation === 'peek'        && 'PEEKING'}
                                        {animatingOperation.operation === 'clear'       && 'CLEARING'}
                                        {animatingOperation.operation === 'deleteQueue' && 'DELETING'}
                                        {animatingOperation.operation === 'queueEmpty'  && 'QUEUE EMPTY'}
                                        {animatingOperation.operation === 'queueFull'   && 'QUEUE FULL'}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Garbage Zone */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px' }}>
                    <div onDragOver={(e) => handleDragOver(e, 'garbage')} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, 'garbage')}
                        style={{ width: '200px', height: '80px', background: dragOverZone === 'garbage' ? 'linear-gradient(135deg, #ff6b6b, #ee5a52)' : 'linear-gradient(135deg, #9ca3af, #6b7280)', borderRadius: '16px', border: dragOverZone === 'garbage' ? '3px dashed #ffffff' : '2px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '1rem', transition: 'all 0.3s ease', cursor: 'default', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.5rem', marginBottom: '5px' }}>{dragOverZone === 'garbage' ? '🔥' : '🗑️'}</div>
                            <div style={{ fontSize: '0.85rem' }}>{dragOverZone === 'garbage' ? selLanguage ? "Άδησε το στοιχείο για διαγραφή" : 'Drop to Delete!' : selLanguage ? "Σύρε το στοιχείο για διαγραφή" : 'Drag Here to Delete'}</div>
                        </div>
                    </div>
                </div>

                {/* Language Selector */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: '600', color: darkMode ? "#fff" : "#0b0a0aff", marginBottom: '8px', letterSpacing: '0.025em', display: 'block' }}>
                        {selLanguage ? "Γλώσσα Προγραμματισμού" : "Programming Language"}
                    </label>
                    <select value={codeLanguage} onChange={(e) => setCodeLanguage(e.target.value)}
                        style={{ padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '12px', fontSize: '0.95rem', background: 'white', transition: 'all 0.2s ease', fontFamily: 'inherit', cursor: 'pointer', width: '200px' }}>
                        <option value="javascript">JavaScript</option>
                        <option value="python">Python</option>
                        <option value="java">Java</option>
                        <option value="cpp">C++</option>
                    </select>
                </div>

                {/* Code Display */}
                <div style={{ background: 'linear-gradient(145deg, #0f172a, #1e293b)', color: '#22d3ee', padding: '24px', borderRadius: '16px', fontFamily: "'Fira Code', monospace", fontSize: '0.8rem', lineHeight: '1.6', overflow: 'auto', maxHeight: '500px', boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.3)', border: '1px solid #334155' }}>
                    <CodeHighlighter code={generateCode()} language={codeLanguage} />
                </div>

                {/* Copy Button */}
                <button onClick={() => { navigator.clipboard.writeText(generateCode()); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className='commonCreateButton'
                    style={{ ...buttonStyle, width: '100%', marginTop: '20px', background: copied ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', transform: copied ? 'translateY(-4px)' : 'translateY(0)', transition: 'all 0.3s ease' }}
                    onMouseOver={(e) => !copied && (e.currentTarget.style.transform = 'translateY(-2px)')}
                    onMouseOut={(e) => !copied && (e.currentTarget.style.transform = 'translateY(0)')}>
                    <Copy size={16} style={{ marginRight: '8px' }} />
                    {copied ? (selLanguage ? ' Αντιγράφηκε!' : ' Copied') : (selLanguage ? ' Αντιγραφή' : ' Copy')}
                </button>
            </div>
        </div>
    );
};

export default QueueBuilder;